import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../../../lib/api';
import type { DateRange, TimeRange } from './useAnalyticsData';

const STALE_TIME_5MIN = 5 * 60 * 1000;
const GC_TIME = 30 * 60 * 1000;

export interface StageScoreValue {
  stageId: string;
  stageName: string;
  position: number;
  leadCount: number;
  weight: number;
  weightedValue: number;
}

export interface PipelineVelocityResponse {
  leadVelocityRate: number | null;
  // Stage-weighted count of the leads in OPEN stages, not a currency amount.
  // Null when the funnel has no open stage past its entry.
  pipelineValue: string | null;
  // The stages that contribute to the score: the open ones past the entry
  // stage. The card reads it to declare its own population — the score covers
  // a SUBSET of the org's leads, and it used to say so nowhere.
  stageValues?: StageScoreValue[];
  leadVelocityGrowth: number | null;
  qualifiedLeadsCurrent: number;
  qualifiedLeadsPrevious: number;
}

export interface TimeInStageItem {
  stageId: string;
  stageName: string;
  color: string;
  position: number;
  // Null when no lead has completed a transit through the stage in the window.
  // A non-nullable number cannot say "not measured yet", so it said "0 days"
  // instead — which the chart drew as the fastest stage in the funnel.
  avgDays: number | null;
  medianDays: number | null;
  // Completed transits through the stage, not leads standing in it. A stage can
  // hold nobody and still have a transit time; that is not a contradiction.
  measuredTransits: number;
  // 'won' and 'lost' are closed outcomes. Their transit time says how fast
  // deals close, not how fast the pipeline moves, so the chart keeps them off
  // its fast/slow scale. Null means OPEN — most real stages carry no role.
  semanticRole?: string | null;
}

// Null is part of the contract, not a transport accident: "nothing closed in
// this window" has no average, and a stage nobody passed through has no outcome
// distribution. Neither is zero.
export interface CycleTimeResponse {
  avgDays: number | null;
  medianDays: number | null;
  totalWonLeads: number;
  distribution: Array<{ range: string; count: number }>;
}

export interface StageDropoffItem {
  stageId: string;
  stageName: string;
  position: number;
  // Distinct leads that were in the stage during the window: the denominator of
  // every percentage below.
  population: number;
  // Leads standing in the stage. Not stale + active: a lead in a terminal stage
  // is reported as won or lost, never as one still in flight.
  totalLeads: number;
  advanced: number;
  advancedPct: number | null;
  regressed: number;
  regressedPct: number | null;
  stale: number;
  stalePct: number | null;
  // Outcome, not location: counts the leads that left this stage for a lost one
  // AND the ones standing in this stage when this stage is the lost one.
  lost: number;
  lostPct: number | null;
  won: number;
  wonPct: number | null;
  active: number;
  activePct: number | null;
}

function toNum(v: unknown): number {
  if (typeof v === 'number') return v;
  return Number.parseFloat(String(v)) || 0;
}

function toNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return v;
  const parsed = Number.parseFloat(String(v));
  return Number.isNaN(parsed) ? null : parsed;
}

export function usePipelineVelocity(organizationId: string | undefined, period: TimeRange) {
  return useQuery<PipelineVelocityResponse>({
    queryKey: ['analytics', 'pipeline', 'velocity', organizationId, period],
    queryFn: async () => {
      const { data } = await analyticsAPI.getPipelineVelocity({
        organizationId: organizationId!,
        period,
      });
      return {
        ...data,
        leadVelocityRate: toNumOrNull(data.leadVelocityRate),
        leadVelocityGrowth: toNumOrNull(data.leadVelocityGrowth),
        qualifiedLeadsCurrent: toNum(data.qualifiedLeadsCurrent),
        qualifiedLeadsPrevious: toNum(data.qualifiedLeadsPrevious),
      };
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_5MIN,
    gcTime: GC_TIME,
  });
}

export function useTimeInStage(
  organizationId: string | undefined,
  templateId: string | undefined,
  dateRange: DateRange,
) {
  return useQuery<TimeInStageItem[]>({
    queryKey: ['analytics', 'pipeline', 'time-in-stage', organizationId, templateId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getTimeInStage({
        organizationId: organizationId!,
        templateId: templateId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return (data as TimeInStageItem[]).map((item) => ({
        ...item,
        avgDays: toNumOrNull(item.avgDays),
        medianDays: toNumOrNull(item.medianDays),
        measuredTransits: toNum(item.measuredTransits),
        position: toNum(item.position),
      }));
    },
    enabled: !!organizationId && !!templateId,
    staleTime: STALE_TIME_5MIN,
    gcTime: GC_TIME,
  });
}

export function useCycleTime(organizationId: string | undefined, dateRange: DateRange) {
  return useQuery<CycleTimeResponse>({
    queryKey: ['analytics', 'pipeline', 'cycle-time', organizationId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getCycleTime({
        organizationId: organizationId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return {
        ...data,
        avgDays: toNumOrNull(data.avgDays),
        medianDays: toNumOrNull(data.medianDays),
        totalWonLeads: toNum(data.totalWonLeads),
      };
    },
    enabled: !!organizationId,
    staleTime: STALE_TIME_5MIN,
    gcTime: GC_TIME,
  });
}

export function useStageDropoff(
  organizationId: string | undefined,
  templateId: string | undefined,
  dateRange: DateRange,
) {
  return useQuery<StageDropoffItem[]>({
    queryKey: ['analytics', 'pipeline', 'stage-dropoff', organizationId, templateId, dateRange],
    queryFn: async () => {
      const { data } = await analyticsAPI.getStageDropoff({
        organizationId: organizationId!,
        templateId: templateId!,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      return (data as StageDropoffItem[]).map((item) => ({
        ...item,
        position: toNum(item.position),
        population: toNum(item.population),
        totalLeads: toNum(item.totalLeads),
        advanced: toNum(item.advanced),
        advancedPct: toNumOrNull(item.advancedPct),
        regressed: toNum(item.regressed),
        regressedPct: toNumOrNull(item.regressedPct),
        stale: toNum(item.stale),
        stalePct: toNumOrNull(item.stalePct),
        lost: toNum(item.lost),
        lostPct: toNumOrNull(item.lostPct),
        won: toNum(item.won),
        wonPct: toNumOrNull(item.wonPct),
        active: toNum(item.active),
        activePct: toNumOrNull(item.activePct),
      }));
    },
    enabled: !!organizationId && !!templateId,
    staleTime: STALE_TIME_5MIN,
    gcTime: GC_TIME,
  });
}
